// The live coverage route (bead hf-e6-commitments-coverage-udt.6).
//
// Two subscriptions, and the split is deliberate.
//
// `coverage.forWeek` feeds BOTH the plan-health glance and the week, from one read:
// the roll-up and the days must describe the same set of needs, and splitting them
// would let the summary answer for a Tuesday the grid below it is no longer showing.
//
// `coverage.needingAttention` is separate because it answers a different question.
// The week is bounded by seven days; a chase list is not — a need three weeks out
// that nobody has taken still wants a person, and the one waiting longest is exactly
// the one a week-scoped list would hide.
//
// ── WHY THIS IS THE ONLY FILE WITH A HOOK IN IT ────────────────────────────────
//
// `CoverageSurfaces.tsx` is a pure component tree over view models and has no idea
// Convex exists — the same arrangement `SettingsRoute` and `NeedRoute` use. It keeps
// the surfaces testable without a provider, and it keeps `App.tsx` — the most
// contended file in the repo — to one line for this whole feature.
//
// ── REACTIVITY IS SUBSCRIPTION, NOT CODE ───────────────────────────────────────
//
// Nothing here polls, refetches or invalidates. A commitment accepted in another
// browser changes a row this query read, so Convex re-runs it and the chips
// re-render. That is the bead's reactivity criterion, and the reason there is no
// cache to go stale is that there is no cache.

import { addLocalWeeks, instant, localDateOf, startOfLocalWeek, timeZone } from "@herefor/domain-kernel";
import { CANONICAL_VOCABULARY } from "@herefor/product-language";
import { useQuery } from "convex/react";
import { useState } from "react";
import type { ReactNode } from "react";

import { coverageQueries } from "../../convex/coverage";
import { CoverageAttention, CoverageWeek, PlanHealth } from "./CoverageSurfaces";
import { toCoverageWeekView, toNeedCoverageView } from "./coverageData";
import type { NeedCoverageView } from "./coverageData";

export interface CoverageRouteProps {
  readonly circleId: string;
  /** The circle's IANA zone, from the plan read. Days are ITS days, not the browser's. */
  readonly timeZone: string;
  /** Builds need routes. An opaque identifier, never anything about a person. */
  readonly publicPlanRef: string;
  /**
   * Fixes "now" for tests. Production passes nothing and reads the clock, because a
   * week view that showed last week after midnight would be worse than useless.
   */
  readonly now?: number;
  /**
   * How much of the week to show.
   *
   * `glance` is the plan-health panel alone, for Today — plan §13.4 puts a "this
   * week summary" near the bottom of a screen that already has its own hierarchy,
   * and a second seven-day grid there would compete with it rather than support it.
   * `week` is the full surface, for the week route itself.
   */
  readonly show?: "glance" | "week";
  readonly renderNeedAction?: (need: NeedCoverageView, dateLabel: string) => ReactNode;
}

/**
 * The organiser's week: how it looks, then what is in it.
 *
 * Health first. The bead's question is "open the week and KNOW instantly what is
 * handled and what needs chasing", and an answer you have to assemble by scanning
 * seven columns is not instant.
 */
export function CoverageRoute({
  circleId,
  timeZone: zoneId,
  publicPlanRef,
  now,
  show = "week",
  renderNeedAction,
}: CoverageRouteProps) {
  const zone = timeZone(zoneId);
  // READ ONCE, ON MOUNT. The clock is impure, and calling it during render would let
  // the week change underneath somebody because React happened to re-render — a
  // surface that silently jumped to next week at midnight, mid-scroll, is worse than
  // one that shows the week you opened. A state initializer runs exactly once.
  const [readAt] = useState(() => now ?? Date.now());
  // The Monday of the CIRCLE's current week. Working this out from the browser's own
  // calendar would show a family in London the wrong week whenever the reader is not
  // in London — and, more quietly, for an hour either side of midnight when they are.
  const currentWeekStart = startOfLocalWeek(localDateOf(instant(readAt), zone));

  // HOW FAR THE READER HAS MOVED, in weeks (bead hf-rtf).
  //
  // Component state rather than a path segment, and that is a decision rather than
  // the easy option: `App.tsx:readBrowserRoute` strips search and hash on every read
  // because URLs here carry opaque identifiers and nothing else, so a week could only
  // live in the path — and a path segment makes a week bookmarkable and shareable,
  // which plan §13.6 (Week is SECONDARY to Agenda) gives no reason to want yet. The
  // segment can be added later; a URL shape that has shipped cannot be taken back.
  //
  // NO NUMERIC BOUND, deliberately. The obvious worry is a pager that walks a plan's
  // whole history one week at a time — but every need it could reach is already in
  // `domain/plan.read`, which the same membership reads in ONE query on the screen
  // they arrived from. A cap would therefore restrict nothing except how a person
  // looks at what they may already see, and any number I picked would be one nobody
  // could explain to a family.
  const [weekOffset, setWeekOffset] = useState(0);
  const weekStartLocal =
    weekOffset === 0 ? currentWeekStart : addLocalWeeks(currentWeekStart, weekOffset);

  const result = useQuery(coverageQueries.forWeek, { circleId, weekStartLocal });
  // A SECOND query, deliberately, because it answers a different question. The week
  // is bounded by seven days; the chase list is not — a need three weeks out that
  // nobody has taken still wants a person, and scoping it to the visible week would
  // hide exactly the work that has been quietly waiting longest. Its ORDER is the
  // server's (please recheck, then soonest) and is never reapplied here.
  // "skip" rather than a discarded result: the glance does not render a chase list,
  // and subscribing to one anyway would keep a query live for a surface nobody sees.
  const attention = useQuery(
    coverageQueries.needingAttention,
    show === "week" ? { circleId } : "skip",
  );

  // `undefined` is Convex still loading. The shell already owns the loading and
  // route-unavailable surfaces, so this renders nothing rather than inventing a
  // second spinner.
  if (result === undefined) return null;
  if (!result.ok) return null;

  const week = toCoverageWeekView(result.value, { publicPlanRef });
  const chasing = !attention?.ok
    ? []
    : attention.value.map((need) => toNeedCoverageView(need, zone, { publicPlanRef }));

  if (show === "glance") return <PlanHealth health={week.health} />;

  return (
    <>
      <PlanHealth health={week.health} />
      <CoverageWeek
        headingLevel={1}
        isCurrentWeek={weekOffset === 0}
        onChangeWeek={(step) => {
          setWeekOffset((current) => current + step);
        }}
        onReturnToThisWeek={() => {
          setWeekOffset(0);
        }}
        week={week}
        {...(renderNeedAction === undefined ? {} : { renderNeedAction })}
      />
      <CoverageAttention heading={CANONICAL_VOCABULARY.helpNeeded} needs={chasing} />
    </>
  );
}
